// import { index } from "fp-ts/lib/Array";
import { fromNullable, some, none, Option, fromEither } from 'fp-ts/lib/Option';
import { query } from 'sdi/shape';
import { OsmResourceId } from './qa';
import { remoteKey, done } from '../events/osm';
import {
    OsmElementNode,
    OsmElementWay,
    OsmElementRelation,
    posOsmElementNode,
    OsmElement,
    OsmType,
    RelationMember,
    OsmChange,
    OsmElementWayIO,
    OsmElementRelationIO,
    TagDict,
} from '../remote';
import { Point, LineString, Polygon } from 'sdi/source';
import { mapOption, catOptions } from 'fp-ts/lib/Array';
import * as proj from 'ol/proj';
import { uniqString } from 'sdi/util';
import { Diff, diffByWord } from '../port/lcs';
import { fst } from 'sdi/lib';
import { Coordinate } from 'ol/coordinate';

const getTag = (el: OsmElement, tag: string) =>
    fromNullable(el.tags).chain(tags => fromNullable(tags[tag]));

const isWayPolygon = (way: OsmElementWay) => {
    if (getTag(way, 'area').getOrElse('no') === 'yes') {
        return true;
    }
    // TODO: add more checks (see https://wiki.openstreetmap.org/wiki/Way )
    return false;
};

const isWayLine = (way: OsmElementWay) => !isWayPolygon(way);

export const hasOsmRemote = (key: string) =>
    key in query('qa/osm/remote-state');

export const getOsmRemote = (key: string) =>
    fromNullable(query('qa/osm/remote-state')[key]);

// const getRemotePoints = (

// )

interface OsmGeometries {
    points: Point[];
    lines: LineString[];
    polygons: Polygon[];
}

const coord = (x: number, y: number) => proj.fromLonLat([x, y]);

const point = (x: number, y: number): Point => ({
    type: 'Point',
    coordinates: coord(x, y),
});

const line = (coords: Coordinate[]): LineString => ({
    type: 'LineString',
    coordinates: coords,
});

const polygon = (coords: Coordinate[]): Polygon => ({
    type: 'Polygon',
    coordinates: [coords],
});

const getGeomFromNode = (node: OsmElementNode): OsmGeometries => ({
    lines: [],
    polygons: [],
    points: posOsmElementNode(node)
        .map(({ lat, lon }) => [point(lon, lat)])
        .getOrElse([]),
});

const getGeomFromWay = (way: OsmElementWay): OsmGeometries => {
    if (way.nodes !== undefined) {
        const coords = mapOption(
            mapOption(way.nodes, findAnyNode),
            posOsmElementNode
        ).map(({ lon, lat }) => coord(lon, lat));
        if (isWayPolygon(way)) {
            return { points: [], lines: [], polygons: [polygon(coords)] };
        }
        return { points: [], lines: [line(coords)], polygons: [] };
    }
    return { points: [], lines: [], polygons: [] };
};

const filterMember = (t: OsmType) => (ms: RelationMember[]) =>
    ms.filter(m => m.type === t);
const filterMemberNode = filterMember('node');
const filterMemberWay = filterMember('way');
// const filterMemberRelation = filterMember('relation')

const getGeomFromRelation = (relation: OsmElementRelation): OsmGeometries => {
    const points = mapOption(
        mapOption(filterMemberNode(relation.members), m => findAnyNode(m.ref)),
        posOsmElementNode
    ).map(node => point(node.lon, node.lat));

    const lines = mapOption(filterMemberWay(relation.members), m =>
        findAnyWay(m.ref)
    )
        .filter(isWayLine)
        .map<LineString>(way => {
            if (way.nodes) {
                const coords = mapOption(
                    mapOption(way.nodes, findAnyNode),
                    posOsmElementNode
                ).map(({ lon, lat }) => coord(lon, lat));
                return line(coords);
            }
            return line([]);
        });

    const polygons = mapOption(
        filterMemberWay(relation.members).filter(m => m.role === 'outer'), // TODO: process holes
        m => findAnyWay(m.ref)
    )
        .filter(isWayPolygon)
        .map<Polygon>(way => {
            if (way.nodes) {
                const coords = mapOption(
                    mapOption(way.nodes, findAnyNode),
                    posOsmElementNode
                ).map(({ lon, lat }) => coord(lon, lat));
                return polygon(coords);
            }
            return polygon([]);
        });

    return { points, lines, polygons };
};

const findNode = (id: number, version: number) =>
    fromNullable(query('qa/osm/nodes')[id.toString()]).chain(versions =>
        fromNullable(versions.find(n => n.version === version))
    );

const findAnyNode = (id: number) =>
    fromNullable(query('qa/osm/nodes')[id.toString()])
        .chain(versions => (versions.length > 0 ? some(versions) : none))
        .map(versions => versions[versions.length - 1]);

const findWay = (id: number, version: number) =>
    fromNullable(query('qa/osm/ways')[id.toString()]).chain(versions =>
        fromNullable(versions.find(n => n.version === version))
    );

const findAnyWay = (id: number) =>
    fromNullable(query('qa/osm/ways')[id.toString()])
        .chain(versions => (versions.length > 0 ? some(versions) : none))
        .map(versions => versions[versions.length - 1]);

const findRelation = (id: number, version: number) =>
    fromNullable(query('qa/osm/relations')[id.toString()]).chain(versions =>
        fromNullable(versions.find(n => n.version === version))
    );

export const getOsmRemoteGeometries = ({ id, type, version }: OsmResourceId) =>
    getOsmRemote(remoteKey(type, id, version))
        .chain(done)
        .chain(_ods => {
            switch (type) {
                case 'node':
                    return findNode(id, version).map(getGeomFromNode);
                case 'way':
                    return findWay(id, version).map(getGeomFromWay);
                case 'relation':
                    return findRelation(id, version).map(getGeomFromRelation);
            }
        });

const findNodeInChange = (change: OsmChange, id: number) => {
    const modified = change.modify.find(
        element => element.type === 'node' && element.id === id
    );
    if (modified !== undefined) {
        return some(modified as OsmElementNode);
    }
    const created = change.create.find(
        element => element.type === 'node' && element.id === id
    );
    if (created !== undefined) {
        return some(created as OsmElementNode);
    }
    const deleted = change.delete.find(
        element => element.type === 'node' && element.id === id
    );
    if (deleted !== undefined) {
        return some(deleted as OsmElementNode);
    }
    return none;
};

const revertNode =
    (change: OsmChange) =>
    (node: OsmElementNode): Option<OsmElementNode> => {
        const modified = change.modify.find(
            element => element.type === 'node' && element.id === node.id
        );
        if (modified !== undefined) {
            return some(modified as OsmElementNode);
        }
        const created = change.create.find(
            element => element.type === 'node' && element.id === node.id
        );
        if (created !== undefined) {
            return none;
        }
        const deleted = change.delete.find(
            element => element.type === 'node' && element.id === node.id
        );
        if (deleted !== undefined) {
            return some(deleted as OsmElementNode);
        }
        return some(node);
    };

const revertWay = (change: OsmChange) => (way: OsmElementWay) => {
    const nodes = fromNullable(
        change.modify.find(
            element => element.type === 'way' && element.id === way.id
        )
    )
        .chain(w => fromEither(OsmElementWayIO.decode(w)))
        .map(w => w.nodes)
        .getOrElse(way.nodes);

    if (nodes !== undefined) {
        return some({
            ...way,
            nodes,
        });
    }

    return none;
};

/**
 * First we look in the changeset for a possible changed node,
 * then we fall back to known nodes (without version)
 */
const getRevertedWayCoords = (change: OsmChange, nodes: number[]) => {
    const ns: Option<OsmElementNode>[] = [];
    const rn = revertNode(change);
    for (const n of nodes) {
        const o = findNodeInChange(change, n).map(rn).getOrElse(findAnyNode(n));
        ns.push(o);
    }
    return mapOption(catOptions(ns), posOsmElementNode).map(({ lon, lat }) =>
        coord(lon, lat)
    );
};

const getGeomFromRevertedWay =
    (change: OsmChange) =>
    (way: OsmElementWay): OsmGeometries => {
        if (way.nodes !== undefined) {
            const coords = getRevertedWayCoords(change, way.nodes);
            if (isWayPolygon(way)) {
                return { points: [], lines: [], polygons: [polygon(coords)] };
            }
            return { points: [], lines: [line(coords)], polygons: [] };
        }
        return { points: [], lines: [], polygons: [] };
    };

const revertRelation =
    (change: OsmChange) => (relation: OsmElementRelation) => {
        const members = fromNullable(
            change.modify.find(
                element =>
                    element.type === 'relation' && element.id === relation.id
            )
        )
            .chain(r => fromEither(OsmElementRelationIO.decode(r)))
            .map(r => r.members)
            .getOrElse(relation.members);

        return {
            ...relation,
            members,
        };
    };

const getGeomFromRevertedRelation =
    (change: OsmChange) =>
    (relation: OsmElementRelation): OsmGeometries => {
        const points = mapOption(
            mapOption(filterMemberNode(relation.members), m =>
                findAnyNode(m.ref).chain(revertNode(change))
            ),
            posOsmElementNode
        ).map(node => point(node.lon, node.lat));

        const lines = mapOption(filterMemberWay(relation.members), m =>
            findAnyWay(m.ref).chain(revertWay(change))
        )
            .filter(isWayLine)
            .map<LineString>(way => {
                if (way.nodes) {
                    const coords = getRevertedWayCoords(change, way.nodes);
                    return line(coords);
                }
                return line([]);
            });

        const polygons = mapOption(
            filterMemberWay(relation.members).filter(m => m.role === 'outer'), // TODO: process holes
            m => findAnyWay(m.ref).chain(revertWay(change))
        )
            .filter(isWayPolygon)
            .map<Polygon>(way => {
                if (way.nodes) {
                    const coords = getRevertedWayCoords(change, way.nodes);
                    return polygon(coords);
                }
                return polygon([]);
            });

        return { points, lines, polygons };
    };

export const revertOsmRemoteGeometries = (
    { id, type, version }: OsmResourceId,
    change: OsmChange
) =>
    getOsmRemote(remoteKey(type, id, version))
        .chain(done)
        .chain(_ods => {
            switch (type) {
                case 'node':
                    return findNode(id, version)
                        .chain(revertNode(change))
                        .map(getGeomFromNode);
                case 'way':
                    return findWay(id, version)
                        .chain(revertWay(change))
                        .map(getGeomFromRevertedWay(change));
                case 'relation':
                    return findRelation(id, version)
                        .map(revertRelation(change))
                        .map(getGeomFromRevertedRelation(change));
            }
        });

interface DiffOpCreate {
    op: 'create';
    key: string;
    value: string;
}

interface DiffOpModify {
    op: 'modify';
    key: string;
    value: string;
    oldValue: string;
    diff: Diff;
}

interface DiffOpDelete {
    op: 'delete';
    key: string;
    value: string;
}

interface DiffOpNone {
    op: 'none';
    key: string;
    value: string;
}

export type DiffEntry = DiffOpCreate | DiffOpModify | DiffOpDelete | DiffOpNone;

const diffOp =
    (a: TagDict, b: TagDict) =>
    (key: string): DiffEntry => {
        if (key in a && !(key in b)) {
            return {
                key,
                op: 'delete',
                value: a[key],
            };
        } else if (!(key in a) && key in b) {
            return {
                key,
                op: 'create',
                value: b[key],
            };
        } else if (key in a && key in b && a[key] !== b[key]) {
            const value = b[key];
            const oldValue = a[key];
            const diff = diffByWord(oldValue, value);
            // const diff = diffByWord(oldValue, value);
            return {
                key,
                op: 'modify',
                value,
                oldValue,
                diff,
            };
        }
        return {
            key,
            op: 'none',
            value: b[key],
        };
    };

export const diffTags = (a: TagDict, b: TagDict): DiffEntry[] =>
    uniqString(Object.keys(a).concat(Object.keys(b)))
        .sort()
        .map(diffOp(a, b))
        .sort((diff1, diff2) => {
            const a = diff1.op === 'none' ? 1 : 0;
            const b = diff2.op === 'none' ? 1 : 0;
            return a - b;
        });

export const getForm = () => fromNullable(query('qa/osm/tags'));

export const getFormId = () => getForm().map(f => f.issueId);

export const getFormComment = () =>
    getForm()
        .map(f => f.comment)
        .getOrElse('');

export const getFormSource = () =>
    getForm()
        .map(f => f.source)
        .getOrElse('');

export const getValidFormComment = () => {
    return getFormComment().split(' ').length >= 2
        ? some(getFormComment())
        : none;
};

export const getFormKey = (index: number) =>
    getForm()
        .chain(f => fromNullable(f.tags[index]))
        .map(tag => fst(tag.pair))
        .getOrElse('');

export const getFormValue = (index: number) =>
    getForm()
        .chain(f => fromNullable(f.tags[index]))
        .map(tag => fst(tag.pair))
        .getOrElse('');
